// Rug Room Shots: "Regenerate selected" dialog (Review tab, SPEC 10).
// selection is { mode: 'ids', genIds: [...] } (ticked rows) or
// { mode: 'filter', filter: { state, style, shape, reason, allAttempts } } ("all N matching this filter"; with
// allAttempts the backend maps every matching attempt to its rug's latest attempt in the job).
//
// 1. POST jobs/{id}/regenerate/preview (read-only) with the room options gives the eligible rows
//    (genIds), the totals per speed, and every skipped row with its reason. It is re-read whenever an
//    option changes; Confirm stays disabled until the preview on screen was made with the options on
//    screen (previewOpts), so a stale preview can never be confirmed.
// 2. POST jobs/{id}/regenerate commits exactly preview.genIds with expectedCount / expectedUsd, which
//    the backend re-checks inside its transaction (409 selection_changed carries a fresh preview).
//    clientRef is made once per open dialog: a repeat (double click, or confirming again after a
//    timeout) is refused with 409 already_submitted and the first result, which counts as success.
// The parent mounts this dialog only while it is open, so every open starts from fresh state and a
// fresh clientRef.
// 2026-10-21 build spec 4.3 / 4.6 / 9.3: Room "Choose the room for each group" (one or more rooms per style x shape
// group of the preview; a group without a choice keeps each rug's room, as "Same room"), "Also send each rug's
// detail photo" (detailMode 'default'), and the skip reasons no_detail, no_room_rule and scene_room_style.
// The stale-preview check also compares the detail mode and the normalised room choices.
// Review fixes 2026-10-21: "Also regenerate N approved images" starts OFF when any ticked row was an earlier attempt
// (fromEarlierAttempts: that tick selects the rug's CURRENT image, often an approved one the person never looked at);
// each group of "Choose the room for each group" carries its rugs' room-type letters (roomLetters) for the picker and
// its own "not the usual room type" count.
// AI check (SPEC_AICHECK 4.10 / 5.6): the selection filter can carry ai / aiBelow / aiAtLeast; the preview lists the
// AI's suggested note of each eligible image (aiNotes). "Use the AI's notes" sends useAiNotes: each image with an AI
// note gets its own note in the prompt, the others the common note above. Notes edited here go as noteOverrides
// (only the edited rows). Notes never change the price, so they are not part of the stale-preview check.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Alert, CircularProgress, Stack,
  Typography, FormControlLabel, Checkbox, RadioGroup, Radio, Chip, Box, Paper, Tooltip, Collapse,
} from '@mui/material';
import { MdExpandMore, MdExpandLess } from 'react-icons/md';
import rrsApi from './api';
import RoomPicker from './RoomPicker';
import {
  primaryButtonSx, fmtUsd, fmtInt, errText, isDailyLimit, dailyLimitText, overBudget, overBudgetText, signalSpend,
  shapeName, StyleChip, serverText, conflictCode, ImageLightbox, AI_STATE,
} from './common';
import { SpeedChooser, isBatch, unavailableText, batchUnavailableFromError, SPEED_FAST } from './speed';
import { NOT_READY_TEXT } from './DetailPhotoBlock';
import { groupLettersSpec, describeLetters } from './roomRules';

const STATUS_CHIPS = [
  { key: 'pending', label: 'needs review' },
  { key: 'approved', label: 'approved' },
  { key: 'rejected', label: 'rejected' },
  { key: 'failed', label: 'failed' },
  { key: 'unknown', label: 'unknown' },
  { key: 'cancelled', label: 'cancelled' },
];

// Skip reasons in the backend's order (build spec 5.2).
const SKIP_TEXT = {
  not_found: (n) => `${n} no longer exist`,
  other_job: (n) => `${n} belong to another job`,
  in_progress: (n) => `${n} still queued, generating or at Google`,
  not_latest: (n) => `${n} have a newer attempt`,
  busy_target: (n) => `${n} already have another attempt in progress`,
  approved: (n) => `${n} approved (tick below to include them)`,
  no_detail: (n) => `${n} have no detail photo on the image server (upload one in the single Regenerate dialog)`,
  room_not_active: (n) => `${n} use a room that is no longer active (choose Least-used room to include them)`,
  no_base: (n) => `${n} use a room with no base image`,
  no_room: (n) => `${n} have no active room of their current style and shape`,
  no_room_rule: (n) => `${n} have no active room of the right type for their size or pile (for example no dining room for a large round)`,
  scene_room_style: (n) => `${n} are sunroom shots (they need a Sunroom room) or main shots of rugs that also have a sunroom shot (they cannot use one), and none of the chosen rooms fits`,
};
const SKIP_ORDER = Object.keys(SKIP_TEXT);

const TIMEOUT_TEXT = 'The server did not answer in time. It may still have queued them. The list has been refreshed. '
  + 'Confirming again is safe: a repeat is refused if the first one went through.';

const MAX_CHOICE_GROUPS = 50;
// Choose-mode refusals of the whole request (a chosen room is unknown, retired, of another shape or has no base).
const ROOM_CHOICE_CODES = ['room_not_found', 'room_not_active', 'room_shape_mismatch', 'no_base'];
const MAX_ROOMS_PER_GROUP = 20;

const makeClientRef = () => `rb-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10).padEnd(8, '0')}`;
const groupKey = (styleCode, shapeCode) => `${styleCode}|${shapeCode}`;
const AI_NOTES_PAGE = 100;

// One AI note (memoised: typing in one row re-renders only that row).
const AiNoteRow = React.memo(({
  genId, itemId, score, state, value, edited, disabled, onEdit,
}) => {
  const info = AI_STATE[state] || null;
  // Only an edited note is checked here (the server falls back to the common note for a too-short AI note).
  const tooShort = edited && value.trim().length < 3;
  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'flex-start' }}>
      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ width: { sm: 200 }, flex: '0 0 auto', pt: { sm: 0.75 } }}>
        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }} noWrap title={itemId}>{itemId || `gen ${genId}`}</Typography>
        {score !== null && score !== undefined && (
          <Chip
            size="small" label={`AI ${score}`} color={info ? info.color : 'default'}
            variant={info && info.color !== 'default' ? 'filled' : 'outlined'} sx={{ height: 20, fontWeight: 700 }}
          />
        )}
        {edited && <Typography variant="caption" color="text.secondary">edited</Typography>}
      </Stack>
      <TextField
        fullWidth size="small" multiline maxRows={4} value={value} disabled={disabled}
        onChange={(e) => onEdit(genId, e.target.value.slice(0, 1000))}
        error={tooShort} helperText={tooShort ? 'At least 3 characters.' : null}
        inputProps={{ 'aria-label': `AI note for ${itemId || `gen ${genId}`}` }}
      />
    </Stack>
  );
});
AiNoteRow.displayName = 'AiNoteRow';

// { 'MC|RO': ['MC26ROL01'] } -> [{ styleCode, shapeCode, roomCodes }] sorted by group, codes sorted (the compare form).
const normaliseChoices = (map) => Object.keys(map || {}).sort()
  .map((k) => {
    const [styleCode, shapeCode] = k.split('|');
    return { styleCode, shapeCode, roomCodes: [...new Set(map[k] || [])].sort() };
  })
  .filter((c) => c.roomCodes.length > 0);

const sameOpts = (a, b) => !!(a && b && a.roomMode === b.roomMode
  && a.moveStyleChanged === b.moveStyleChanged && a.includeApproved === b.includeApproved
  && a.detailMode === b.detailMode
  && JSON.stringify(a.roomChoices || []) === JSON.stringify(b.roomChoices || []));

// The request fields for the options on screen. "Choose" without any choice is the same as "Same room"
// (a group without a choice keeps each rug's room), so it is sent as "same" until a room is chosen.
const optionFields = (o) => {
  const choose = o.roomMode === 'choose' && o.roomChoices.length > 0;
  return {
    roomMode: o.roomMode === 'choose' && !choose ? 'same' : o.roomMode,
    roomChoices: choose ? o.roomChoices : undefined,
    moveStyleChanged: o.moveStyleChanged,
    includeApproved: o.includeApproved,
    detailMode: o.detailMode,
  };
};

const BulkRegenerateDialog = ({
  open, onClose, jobId, selection, jobDetail, speeds, config, styles, notify, onSubmitting, onDone, onRefresh, defaultDetail,
  fromEarlierAttempts, defaultUseAiNotes = false, defaultNote,
}) => {
  const features = (config && config.features) || {};
  const detailFeature = features.detailPhotos === true;
  const shapes = (config && config.shapes) || undefined;
  const [preview, setPreview] = useState(null);
  const [previewOpts, setPreviewOpts] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [changedMsg, setChangedMsg] = useState('');
  // Kept apart from `error`: loadPreview clears `error`, and this notice must stay up after the refresh.
  const [timeoutMsg, setTimeoutMsg] = useState('');
  const [roomMode, setRoomMode] = useState('same');
  const [moveStyleChanged, setMoveStyleChanged] = useState(true);
  // Ticked rows (ids) used to mean "the images I looked at", so approved ones were included by default. A tick on an
  // earlier attempt selects the rug's current image instead, which is often approved: then they start excluded.
  const [includeApproved, setIncludeApproved] = useState(() => !!(selection && selection.mode === 'ids' && !fromEarlierAttempts));
  const [detailOn, setDetailOn] = useState(() => !!defaultDetail && detailFeature);
  const [choices, setChoices] = useState({}); // { 'MC|RO': ['MC26ROL01', ...] }
  const [chooseGroup, setChooseGroup] = useState(null); // the group whose room picker is open
  const [chooseDraft, setChooseDraft] = useState([]);
  const [roomPreview, setRoomPreview] = useState(null);
  // A default note (Regenerate by AI result) counts as touched, so "Retry after failure" never replaces it.
  const [note, setNote] = useState(() => (defaultNote ? String(defaultNote).slice(0, 1000) : ''));
  const [noteTouched, setNoteTouched] = useState(!!defaultNote);
  const [inPrompt, setInPrompt] = useState(false);
  // AI notes (SPEC_AICHECK 5.6): genId -> the person's edited text (absent = the AI's note unchanged).
  const [useAiNotes, setUseAiNotes] = useState(!!defaultUseAiNotes);
  const [showAiNotes, setShowAiNotes] = useState(false);
  const [aiNotesShown, setAiNotesShown] = useState(AI_NOTES_PAGE);
  const [noteEdits, setNoteEdits] = useState({});
  const [speed, setSpeed] = useState(SPEED_FAST);
  const [busy, setBusy] = useState(false);
  const [clientRef] = useState(makeClientRef);
  const speedSet = useRef(false);
  const seq = useRef(0);

  const roomChoices = useMemo(() => normaliseChoices(choices), [choices]);
  const detailMode = detailOn && detailFeature ? 'default' : 'none';
  const opts = useMemo(() => ({
    roomMode, moveStyleChanged, includeApproved, detailMode, roomChoices: roomMode === 'choose' ? roomChoices : [],
  }), [roomMode, moveStyleChanged, includeApproved, detailMode, roomChoices]);

  const loadPreview = useCallback(async () => {
    if (!jobId || !selection) return;
    const my = seq.current + 1;
    seq.current = my;
    const used = { ...opts };
    setLoading(true);
    setError('');
    try {
      const res = await rrsApi.regeneratePreview(jobId, { selection, ...optionFields(used) });
      if (my !== seq.current) return;
      setPreview(res);
      setPreviewOpts(used);
      if (!speedSet.current && res && res.defaultSpeed) {
        speedSet.current = true;
        setSpeed(res.defaultSpeed);
      }
    } catch (e) {
      // Choose-mode refusals (room_not_found / room_not_active / room_shape_mismatch / no_base) carry the server's words.
      if (my === seq.current) {
        const code = conflictCode(e);
        setError(code && ROOM_CHOICE_CODES.includes(code) ? `${serverText(e)} Change the chosen rooms.` : serverText(e));
      }
    } finally {
      if (my === seq.current) setLoading(false);
    }
  }, [jobId, selection, opts]);

  useEffect(() => { if (open) loadPreview(); }, [open, loadPreview]);
  useEffect(() => { if (!open) setTimeoutMsg(''); }, [open]);

  // "Retry after failure" when every eligible row failed or ended Unknown (as the single dialog).
  useEffect(() => {
    if (noteTouched || !preview || !preview.count) return;
    const bs = preview.byStatus || {};
    if ((Number(bs.failed) || 0) + (Number(bs.unknown) || 0) === preview.count) setNote('Retry after failure');
  }, [preview, noteTouched]);

  // The page's speed list (prices from the job snapshot via `job`), narrowed by the preview's own
  // availability so Half price cannot be picked when the backend just said it is unavailable.
  const chooserSpeeds = useMemo(() => (speeds || []).map((s) => {
    const p = preview && Array.isArray(preview.speeds) ? preview.speeds.find((x) => x.code === s.code) : null;
    if (!p || p.available !== false) return s;
    return { ...s, available: false, reason: s.reason || unavailableText(p.unavailableReason, p.unavailableMessage) };
  }), [speeds, preview]);

  const count = preview ? Number(preview.count) || 0 : 0;
  const requested = preview ? Number(preview.requested) || 0 : 0;
  const sel = preview && Array.isArray(preview.speeds) ? preview.speeds.find((s) => s.code === speed) : null;
  const half = isBatch(speed);
  const skipped = (preview && preview.skippedCounts) || {};
  const byStatus = (preview && preview.byStatus) || {};
  const fresh = sameOpts(previewOpts, opts);
  const noteOk = note.trim().length >= 3;
  const approvedSkipped = preview ? Number(preview.approvedSkipped) || 0 : 0;
  const approvedCount = includeApproved ? (Number(byStatus.approved) || 0) : approvedSkipped;
  const styleChangedCount = preview ? Number(preview.styleChangedCount) || 0 : 0;
  const roomChanges = preview ? Number(preview.roomChanges) || 0 : 0;
  const unknownCount = preview ? Number(preview.unknownCount) || 0 : 0;
  const groups = preview && Array.isArray(preview.groups) ? preview.groups : [];
  const detailCount = preview ? Number(preview.detailCount) || 0 : 0;
  const detailNotChecked = preview ? Number(preview.detailNotCheckedCount) || 0 : 0;
  const noDetail = Number(skipped.no_detail) || 0;
  const ruleMismatch = preview ? Number(preview.ruleMismatchCount) || 0 : 0;
  const shapeMismatch = preview ? Number(preview.baseShapeMismatchCount) || 0 : 0;
  const nothing = !!preview && fresh && !loading && count === 0;

  // AI notes of the eligible rows (always in the preview of a new backend; absent on an older one).
  const aiNotes = useMemo(() => (preview && Array.isArray(preview.aiNotes) ? preview.aiNotes.filter((n) => n && n.genId) : []), [preview]);
  const aiNoteCount = preview ? (Number(preview.aiNoteCount) || aiNotes.length) : 0;
  const aiNoteMissing = preview ? Number(preview.aiNoteMissingCount) || 0 : 0;
  const onEditAiNote = useCallback((genId, text) => setNoteEdits((cur) => ({ ...cur, [genId]: text })), []);
  // Only edited rows of images that are still eligible go to the server (each must be one of genIds).
  const noteOverrides = useMemo(() => {
    if (!useAiNotes || !preview) return [];
    const ids = new Set((preview.genIds || []).map(Number));
    return aiNotes
      .filter((n) => noteEdits[n.genId] !== undefined && noteEdits[n.genId] !== n.note && ids.has(Number(n.genId)))
      .map((n) => ({ genId: n.genId, note: noteEdits[n.genId].trim() }));
  }, [useAiNotes, preview, aiNotes, noteEdits]);
  const badOverride = noteOverrides.some((o) => o.note.length < 3);

  const canConfirm = !!preview && !loading && !busy && count > 0 && noteOk && !!sel && sel.available !== false && fresh
    && !badOverride;

  const finish = (res) => {
    const r = res || {};
    signalSpend();
    const wasHalf = isBatch(r.speed || speed);
    const withDetail = Number(r.detailCount) || 0;
    const withAi = Number(r.aiNoteCount) || 0;
    const withEdited = Number(r.editedNoteCount) || 0;
    notify(
      `Queued ${fmtInt(r.queued)} new attempts${wasHalf ? ' at half price' : ''} (about ${fmtUsd(r.estUsd)}).`
      + `${withDetail ? ` ${fmtInt(withDetail)} with the rug's detail photo.` : ''}`
      + `${withAi ? ` ${fmtInt(withAi)} with the AI's notes.` : ''}`
      + `${withEdited ? ` ${fmtInt(withEdited)} with notes you edited.` : ''}`
      + `${wasHalf ? ' Google usually returns them within hours, at most 24 h.' : ''}`,
      'success',
    );
    if (onDone) onDone(r);
  };

  const submit = async () => {
    if (!canConfirm) return;
    const ids = preview.genIds || [];
    setBusy(true);
    setChangedMsg('');
    setError('');
    setTimeoutMsg('');
    if (onSubmitting) onSubmitting(ids, true);
    try {
      const res = await rrsApi.regenerateMany(jobId, {
        clientRef,
        genIds: ids,
        expectedCount: preview.count,
        expectedUsd: sel.estUsd,
        speed,
        note: note.trim(),
        noteInPrompt: inPrompt,
        ...optionFields(opts),
        selection,
        useAiNotes: useAiNotes && aiNoteCount > 0,
        noteOverrides: noteOverrides.length ? noteOverrides : undefined,
      });
      finish(res);
    } catch (e) {
      const code = e && e.status === 409 && e.body ? e.body.code : null;
      if (code === 'already_submitted') {
        finish((e.body && e.body.current) || {});
      } else if (code === 'selection_changed' && e.body.current) {
        const cur = e.body.current;
        const at = Array.isArray(cur.speeds) ? cur.speeds.find((s) => s.code === speed) : null;
        setPreview(cur);
        setPreviewOpts({ ...opts });
        setChangedMsg(`The selection changed: now ${fmtInt(cur.count)} images, ${fmtUsd(at ? at.estUsd : null)}. Check and confirm again.`);
      } else if (code === 'batch_unavailable') {
        notify(`${batchUnavailableFromError(e)} Choose Fast instead.`, 'warning');
        setSpeed(SPEED_FAST);
        loadPreview();
      } else if (isDailyLimit(e)) {
        // Over the non-admin daily limit: nothing was queued. Show the server's words; no automatic retry.
        setError(dailyLimitText(e));
        signalSpend();
      } else if (!e || !e.status || e.status >= 500) {
        // Possibly a load-balancer timeout while the commit still went through: refresh both,
        // keep the same clientRef so confirming again cannot queue a second set.
        setTimeoutMsg(TIMEOUT_TEXT);
        if (onRefresh) await onRefresh();
        loadPreview();
      } else if (code) {
        // room_not_active / room_not_found / room_shape_mismatch / no_base (a room retired after the preview), ...
        setError(serverText(e));
        loadPreview();
      } else {
        setError(errText(e));
      }
    } finally {
      if (onSubmitting) onSubmitting(ids, false);
      setBusy(false);
    }
  };

  // ---- "Choose the room for each group" ---------------------------------------------------------------
  const chosenGroups = Object.keys(choices).filter((k) => (choices[k] || []).length > 0);
  const openChoose = (g) => {
    setChooseGroup(g);
    setChooseDraft([...(choices[groupKey(g.styleCode, g.shapeCode)] || [])]);
  };
  const applyChoose = (codes) => {
    if (!chooseGroup) return;
    const k = groupKey(chooseGroup.styleCode, chooseGroup.shapeCode);
    setChoices((cur) => {
      const next = { ...cur };
      if (codes && codes.length) next[k] = [...new Set(codes)].slice(0, MAX_ROOMS_PER_GROUP);
      else delete next[k];
      return next;
    });
    setChooseGroup(null);
  };
  const clearChoice = (g) => setChoices((cur) => {
    const next = { ...cur };
    delete next[groupKey(g.styleCode, g.shapeCode)];
    return next;
  });

  const confirmLabel = `Regenerate ${fmtInt(count)}${half ? ' at half price' : ''} · ${fmtUsd(sel ? sel.estUsd : null)}`;
  const filterReason = selection && selection.mode === 'filter' && selection.filter ? selection.filter.reason : null;
  // The AI filter of the selection in words ("Regenerate by AI result").
  const sf = selection && selection.mode === 'filter' && selection.filter ? selection.filter : {};
  let aiPickWords = null;
  if (sf.ai === 'fail') aiPickWords = 'the AI check says Fail';
  else if (sf.ai === 'not_pass') aiPickWords = 'the AI check says Fail or Needs a look';
  else if (sf.ai === 'look') aiPickWords = 'the AI check says Needs a look';
  else if (sf.aiBelow) aiPickWords = `the AI score is below ${sf.aiBelow}`;

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {preview ? `Regenerate ${fmtInt(count)} images` : 'Regenerate selected images'}
        <Typography variant="body2" color="text.secondary">
          Job #{jobId}. Each image gets a new attempt; the current one stays in its history.
          {selection && selection.mode === 'filter' && selection.filter && selection.filter.allAttempts
            ? ' The current image of every rug in the filtered rows is regenerated.' : ''}
          {aiPickWords ? ` Images where ${aiPickWords}.` : ''}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {loading && !preview && <Box sx={{ py: 3, textAlign: 'center' }}><CircularProgress size={24} /></Box>}
        {changedMsg && <Alert severity="warning" sx={{ mb: 1.5 }}>{changedMsg}</Alert>}
        {timeoutMsg && <Alert severity="warning" sx={{ mb: 1.5 }}>{timeoutMsg}</Alert>}
        {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
        {!error && sel && overBudget(config, sel.estUsd) && (
          <Alert severity="warning" sx={{ mb: 1.5 }}>{overBudgetText(config, sel.estUsd)}</Alert>
        )}

        {preview && (
          <>
            {/* Summary */}
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5, flexWrap: 'wrap', rowGap: 0.75 }}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>{fmtInt(count)} will be queued:</Typography>
              {STATUS_CHIPS.filter((c) => Number(byStatus[c.key]) > 0).map((c) => (
                <Chip key={c.key} size="small" variant="outlined" label={`${fmtInt(byStatus[c.key])} ${c.label}`} />
              ))}
              {loading && <CircularProgress size={16} />}
            </Stack>

            {/* Skips */}
            {requested - count > 0 && (
              <Alert severity="info" sx={{ mb: 1.5 }}>
                {fmtInt(requested - count)} of the selected rows will be skipped:
                <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
                  {SKIP_ORDER.filter((k) => Number(skipped[k]) > 0).map((k) => (
                    <li key={k}><Typography variant="body2">{SKIP_TEXT[k](fmtInt(skipped[k]))}</Typography></li>
                  ))}
                </Box>
              </Alert>
            )}

            {unknownCount > 0 && (
              <Alert severity="warning" sx={{ mb: 1.5 }}>
                {fmtInt(unknownCount)} of these ended as Unknown: the previous attempt may already have been billed.
              </Alert>
            )}

            {(approvedSkipped > 0 || includeApproved) && (
              <Box sx={{ mb: 1 }}>
                <FormControlLabel
                  control={<Checkbox checked={includeApproved} onChange={(e) => setIncludeApproved(e.target.checked)} disabled={busy} />}
                  label={`Also regenerate ${fmtInt(approvedCount)} approved images`}
                  sx={{ display: 'flex' }}
                />
                {fromEarlierAttempts && approvedCount > 0 && (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4 }}>
                    A ticked earlier attempt selects the rug&apos;s current image, and {fmtInt(approvedCount)} of those are
                    approved now. They are left out unless you tick this; a new attempt replaces the approved image in
                    &quot;Approved (latest attempt)&quot; downloads until it is approved again.
                  </Typography>
                )}
              </Box>
            )}

            {/* Detail photo (spec 4.3) */}
            {detailFeature ? (
              <Box sx={{ mb: 1 }}>
                <FormControlLabel
                  control={<Checkbox checked={detailOn} onChange={(e) => setDetailOn(e.target.checked)} disabled={busy} />}
                  label="Also send each rug's detail photo"
                  sx={{ display: 'flex' }}
                />
                <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4 }}>
                  The rug&apos;s close-up from the image server goes along as a third image; it helps copy the real edge,
                  binding and pile. Costs about the same.
                  {filterReason === 'rug_binding' ? ' On by default because these rugs were rejected for Rug binding.' : ''}
                </Typography>
                {detailOn && fresh && previewOpts && previewOpts.detailMode === 'default' && (
                  <Typography variant="body2" sx={{ ml: 4, mt: 0.5 }}>
                    {fmtInt(detailCount)} of {fmtInt(detailCount + noDetail)} rugs have one
                    {noDetail ? `; ${fmtInt(noDetail)} will be skipped (no detail photo)` : ''}.
                    {detailNotChecked > 0 ? ` ${fmtInt(detailNotChecked)} are still being looked up: try again in a few minutes.` : ''}
                  </Typography>
                )}
              </Box>
            ) : (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Detail photos: {NOT_READY_TEXT}
              </Typography>
            )}

            {nothing ? (
              <Typography color="text.secondary" sx={{ my: 1 }}>Nothing to regenerate in this selection.</Typography>
            ) : (
              <>
                {/* Note */}
                <TextField
                  fullWidth size="small" label="What should change? (required, logged on every image)" value={note}
                  multiline minRows={2} autoFocus
                  onChange={(e) => { setNoteTouched(true); setNote(e.target.value.slice(0, 1000)); }}
                  error={note.length > 0 && !noteOk}
                  helperText={!noteOk ? 'At least 3 characters.' : ' '}
                  disabled={busy}
                />
                <FormControlLabel
                  control={<Checkbox checked={inPrompt} onChange={(e) => setInPrompt(e.target.checked)} disabled={busy} />}
                  label="Add my note to the prompt"
                />
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                  Off: the same prompt is sent again (the note is only logged). On: your note is appended to the standard prompt.
                </Typography>

                {/* AI notes (SPEC_AICHECK 5.6) */}
                {aiNoteCount > 0 && (
                  <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 1.5, mb: 1.5 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>AI notes</Typography>
                    <FormControlLabel
                      control={<Checkbox checked={useAiNotes} onChange={(e) => setUseAiNotes(e.target.checked)} disabled={busy} />}
                      label="Use the AI's notes"
                    />
                    <Typography variant="caption" color="text.secondary" display="block">
                      Each image with an AI note gets its own note, added to the prompt.
                      {aiNoteMissing > 0
                        ? ` ${fmtInt(aiNoteMissing)} image${aiNoteMissing === 1 ? '' : 's'} without one ${aiNoteMissing === 1 ? 'gets' : 'get'} your note above.`
                        : ''}
                    </Typography>
                    {useAiNotes && (
                      <>
                        <Button
                          size="small" sx={{ mt: 0.5, px: 0.5 }} onClick={() => setShowAiNotes((v) => !v)}
                          endIcon={showAiNotes ? <MdExpandLess /> : <MdExpandMore />}
                        >
                          {showAiNotes ? 'Hide the notes' : 'Show and edit the notes'} ({fmtInt(aiNoteCount)})
                        </Button>
                        {noteOverrides.length > 0 && (
                          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                            {fmtInt(noteOverrides.length)} edited
                          </Typography>
                        )}
                        <Collapse in={showAiNotes} unmountOnExit>
                          <Stack spacing={1} sx={{ mt: 1 }}>
                            {aiNotes.slice(0, aiNotesShown).map((n) => {
                              const v = noteEdits[n.genId] !== undefined ? noteEdits[n.genId] : (n.note || '');
                              return (
                                <AiNoteRow
                                  key={n.genId} genId={n.genId} itemId={n.itemId} score={n.score} state={n.state}
                                  value={v} edited={noteEdits[n.genId] !== undefined && noteEdits[n.genId] !== n.note}
                                  disabled={busy} onEdit={onEditAiNote}
                                />
                              );
                            })}
                          </Stack>
                          {aiNotes.length > aiNotesShown && (
                            <Button size="small" sx={{ mt: 1 }} onClick={() => setAiNotesShown((c) => c + AI_NOTES_PAGE)}>
                              Show {fmtInt(Math.min(AI_NOTES_PAGE, aiNotes.length - aiNotesShown))} more
                            </Button>
                          )}
                        </Collapse>
                        {badOverride && (
                          <Typography variant="caption" color="error.main" display="block">
                            Each edited note needs at least 3 characters.
                          </Typography>
                        )}
                      </>
                    )}
                  </Paper>
                )}

                {/* Room */}
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Room</Typography>
                <RadioGroup value={roomMode} onChange={(e) => setRoomMode(e.target.value)}>
                  <FormControlLabel value="same" control={<Radio size="small" />} label="Same room as each image" disabled={busy} />
                  {(roomMode === 'same' || roomMode === 'choose') && styleChangedCount > 0 && (
                    <FormControlLabel
                      sx={{ ml: 4 }}
                      control={<Checkbox size="small" checked={moveStyleChanged} onChange={(e) => setMoveStyleChanged(e.target.checked)} disabled={busy} />}
                      label={`Move the ${fmtInt(styleChangedCount)} rugs whose style changed to the least-used room of their new style${roomMode === 'choose' ? ' (groups without a chosen room)' : ''}`}
                    />
                  )}
                  <FormControlLabel
                    value="auto" control={<Radio size="small" />} disabled={busy}
                    label="Least-used active room of each rug's current style and room type (a different room when there is one)"
                  />
                  {roomMode === 'auto' && roomChanges > 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 4 }}>{fmtInt(roomChanges)} will change room.</Typography>
                  )}
                  <FormControlLabel
                    value="choose" control={<Radio size="small" />} disabled={busy || groups.length === 0}
                    label="Choose the room for each group"
                  />
                </RadioGroup>
                {roomMode === 'choose' && (
                  <Box sx={{ ml: { xs: 0, sm: 4 }, mb: 1 }}>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>
                      Pick one or more rooms per style and shape. With several rooms, each rug goes to the least-used chosen
                      room of the right type for it. A group left on &quot;Keep each rug&apos;s room&quot; stays as it is.
                    </Typography>
                    <Stack spacing={0.75}>
                      {groups.map((g) => {
                        const k = groupKey(g.styleCode, g.shapeCode);
                        const picked = choices[k] || [];
                        const limitReached = !picked.length && chosenGroups.length >= MAX_CHOICE_GROUPS;
                        const now = (g.currentRooms || []).map((c) => `${c.roomCode} ${fmtInt(c.count)}`).join(', ');
                        const usual = (g.roomLetters || []).some((x) => x && x.letters)
                          ? (g.roomLetters || []).map((x) => `${x && x.letters ? describeLetters(x.letters, config && config.roomLetters) : 'any room type'} ${fmtInt(x ? x.count : 0)}`).join(', ')
                          : '';
                        const groupMismatch = picked.length && fresh ? Number(g.ruleMismatchCount) || 0 : 0;
                        return (
                          <Paper key={k} variant="outlined" sx={{ p: 1, borderRadius: 1.5 }}>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 0.75 }}>
                              <StyleChip code={g.styleCode} styles={styles} />
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {g.styleCode} · {shapeName(g.shapeCode, shapes)} · {fmtInt(g.count)} rug{g.count === 1 ? '' : 's'}
                              </Typography>
                              {now && <Typography variant="caption" color="text.secondary">(now {now})</Typography>}
                              {usual && <Typography variant="caption" color="text.secondary">usual room type: {usual}</Typography>}
                              {groupMismatch > 0 && (
                                <Chip size="small" color="warning" variant="outlined" label={`${fmtInt(groupMismatch)} not the usual room type`} />
                              )}
                              <Box sx={{ flex: 1 }} />
                              {picked.length ? (
                                <>
                                  {picked.map((c) => <Chip key={c} size="small" label={c} sx={{ fontFamily: 'monospace' }} />)}
                                  <Button size="small" onClick={() => openChoose(g)} disabled={busy}>Change</Button>
                                  <Button size="small" onClick={() => clearChoice(g)} disabled={busy}>Keep each rug&apos;s room</Button>
                                </>
                              ) : (
                                <Tooltip title={limitReached ? `Rooms can be chosen for at most ${MAX_CHOICE_GROUPS} groups at a time.` : ''}>
                                  <span>
                                    <Button size="small" variant="outlined" onClick={() => openChoose(g)} disabled={busy || limitReached}>
                                      Keep each rug&apos;s room · choose rooms…
                                    </Button>
                                  </span>
                                </Tooltip>
                              )}
                            </Stack>
                          </Paper>
                        );
                      })}
                    </Stack>
                    {roomChoices.length === 0 && (
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75 }}>
                        No room chosen yet: every rug keeps its room.
                      </Typography>
                    )}
                  </Box>
                )}
                {fresh && ruleMismatch > 0 && (
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    {fmtInt(ruleMismatch)} rug{ruleMismatch === 1 ? '' : 's'} will go to a room that is not the usual type for {ruleMismatch === 1 ? 'it' : 'them'} (for example a dining room for a small round).
                  </Alert>
                )}
                {fresh && shapeMismatch > 0 && (
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    {fmtInt(shapeMismatch)} rug{shapeMismatch === 1 ? '' : 's'} will use a room whose base photo shows an oval rug.
                  </Alert>
                )}

                {/* Speed */}
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 2 }}>Speed</Typography>
                <SpeedChooser value={speed} onChange={setSpeed} speeds={chooserSpeeds} count={preview.count} job={jobDetail} disabled={busy} />
              </>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        {nothing ? (
          <Button onClick={onClose}>Close</Button>
        ) : (
          <>
            <Button onClick={onClose} disabled={busy}>Cancel</Button>
            <Button variant="contained" onClick={submit} disabled={!canConfirm} sx={primaryButtonSx}>
              {busy ? <CircularProgress size={20} color="inherit" /> : confirmLabel}
            </Button>
          </>
        )}
      </DialogActions>

      {/* Room picker for one group (several rooms allowed) */}
      <Dialog open={!!chooseGroup} onClose={() => setChooseGroup(null)} maxWidth="md" fullWidth>
        {chooseGroup && (
          <>
            <DialogTitle>
              Rooms for {chooseGroup.styleCode} · {shapeName(chooseGroup.shapeCode, shapes)} ({fmtInt(chooseGroup.count)} rug{chooseGroup.count === 1 ? '' : 's'})
              <Typography variant="body2" color="text.secondary">
                Tick one or more rooms (up to {MAX_ROOMS_PER_GROUP}); click a photo to see it large. Same style first;
                rooms of another style, not the usual type, or with an oval-rug base photo are marked.
              </Typography>
            </DialogTitle>
            <DialogContent dividers>
              <RoomPicker
                multiple
                shapeCode={chooseGroup.shapeCode}
                styleCode={chooseGroup.styleCode}
                values={chooseDraft}
                onChangeMany={(codes) => setChooseDraft(codes.slice(0, MAX_ROOMS_PER_GROUP))}
                onPreview={(r) => setRoomPreview(r)}
                styles={styles}
                allowedLetters={groupLettersSpec(chooseGroup.roomLetters) || undefined}
                letterNames={config && config.roomLetters}
              />
              {chooseDraft.length >= MAX_ROOMS_PER_GROUP && (
                <Alert severity="info" sx={{ mt: 1 }}>At most {MAX_ROOMS_PER_GROUP} rooms per group.</Alert>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setChooseGroup(null)}>Cancel</Button>
              <Button onClick={() => applyChoose([])}>Keep each rug&apos;s room</Button>
              <Button variant="contained" sx={primaryButtonSx} disabled={!chooseDraft.length} onClick={() => applyChoose(chooseDraft)}>
                {chooseDraft.length ? `Use ${chooseDraft.length === 1 ? chooseDraft[0] : `these ${chooseDraft.length} rooms`}` : 'Use these rooms'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
      <ImageLightbox
        open={!!roomPreview} onClose={() => setRoomPreview(null)}
        items={roomPreview && roomPreview.base ? [{
          key: 'base', label: 'Base room', src: roomPreview.base.largeUrl || roomPreview.base.originalUrl || roomPreview.base.smallUrl,
          downloadUrl: roomPreview.base.originalUrl, downloadLabel: 'Open original',
        }] : []}
        initialKey="base" title={roomPreview ? roomPreview.roomCode : ''}
      />
    </Dialog>
  );
};

export default BulkRegenerateDialog;
